import React, { useMemo, useState } from "react";
import { Box, ListItemButton, ListItemText, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import type {
  Account,
  ImpersonationSelection,
  ImpersonationState,
  Tenant,
} from "../../types";
import {
  createVirtualAdminPersona,
  getWorkingSelection,
  getPreviewAccount,
  getPersonaForAccount,
  updateSelectionWithPersona,
} from "../../selectionState";
import { useSelectorPopover } from "../../useSelectorPopover";
import useGetTenant, {
  isOnboardedTenant,
} from "@features/impersonation-feature/hooks/useGetTenant";
import ClickableSection from "@features/impersonation-feature/components/Panel/ClickableSection";
import SearchPopover from "@features/impersonation-feature/components/Panel/SearchPopover";
import { TENANT_ID_UNDEFINED } from "@features/impersonation-feature/constants";
import TenantAvatar from "@features/impersonation-feature/components/Panel/TenantAvatar";
import useGetTenantCurrent from "@features/unified-user-header-feature/hooks/useGetTenantCurrent";

interface TenantSelectorProps {
  pending: ImpersonationSelection | null;
  working: ImpersonationSelection | null;
  origin?: ImpersonationSelection | null;
  setImpersonationState: React.Dispatch<
    React.SetStateAction<ImpersonationState>
  >;
}

const LISTBOX_ID = "impersonation-tenant-options";

const srOnlySx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

type TenantOption = {
  id: string;
  name: string | null;
  accountId: number | null;
  accountName: string | null;
  axNumber: string | null;
  rowKey: string | null;
};

const notOnboardedOption: TenantOption = {
  id: TENANT_ID_UNDEFINED,
  name: "Not Onboarded",
  accountId: null,
  accountName: "Preview a Customer Account",
  axNumber: null,
  rowKey: null,
};

function toAccount(
  account: Pick<Account, "id" | "name" | "axNumber" | "isPrimary" | "rowKey">,
): Account {
  return {
    id: account.id,
    name: account.name,
    axNumber: account.axNumber,
    isPrimary: account.isPrimary,
    rowKey: account.rowKey,
  };
}

function toTenantOption({
  tenantId,
  tenantName,
  account,
}: {
  tenantId: string | null | undefined;
  tenantName: string | null | undefined;
  account:
    | Pick<Account, "id" | "name" | "axNumber" | "rowKey">
    | null
    | undefined;
}): TenantOption {
  return {
    id: tenantId ?? TENANT_ID_UNDEFINED,
    name: tenantName ?? "Not Onboarded",
    accountId: account?.id ?? null,
    accountName: account?.name ?? "Preview a Customer Account",
    axNumber: account?.axNumber ?? null,
    rowKey: account?.rowKey ?? null,
  };
}

function prependOptionIfMissing(
  options: TenantOption[],
  option: TenantOption | null,
): void {
  if (
    !option ||
    options.some((existingOption) => existingOption.id === option.id)
  ) {
    return;
  }

  options.unshift(option);
}

export default function TenantSelector({
  pending,
  working,
  setImpersonationState,
  origin,
}: TenantSelectorProps) {
  const [searchString, setSearchString] = useState("");
  const {
    anchorEl,
    isOpen,
    searchInputRef,
    listRef,
    closeSelector,
    handleOpen,
    handleSummaryKeyDown,
    handleSearchKeyDown,
    handleOptionKeyDown,
  } = useSelectorPopover({ pending, setImpersonationState });

  const { data: effectiveTenant } = useGetTenantCurrent();

  const pEffectiveTenant = useMemo(() => {
    if (!effectiveTenant) return null;

    const effectiveTenantAccounts =
      effectiveTenant.accounts
        ?.filter(
          (
            account,
          ): account is typeof account & { autotaskAccountId: number } =>
            account.autotaskAccountId != null,
        )
        .map((account) =>
          toAccount({
            id: account.autotaskAccountId,
            name: account.name ?? null,
            axNumber: account.axNumber ?? null,
            isPrimary: account.isPrimaryAccount ?? false,
            rowKey: null,
          }),
        ) ?? [];

    return {
      id: effectiveTenant.tenantId ?? TENANT_ID_UNDEFINED,
      name: effectiveTenant.name ?? null,
      accounts: effectiveTenantAccounts,
      orgUnitId: null,
      onBoardingStatus: effectiveTenant.onboardingStatus ?? null,
    };
  }, [effectiveTenant]);

  const { tenants, notOnboarded, isInitialLoading } =
    useGetTenant(searchString);

  const isWorkingTenantOnboarded = isOnboardedTenant(
    working?.tenant?.onBoardingStatus ?? null,
  );
  const hasWorkingTenantId =
    (working?.tenant?.id ?? TENANT_ID_UNDEFINED) !== TENANT_ID_UNDEFINED;

  const workingOption: TenantOption =
    hasWorkingTenantId && isWorkingTenantOnboarded
      ? toTenantOption({
          tenantId: working?.tenant?.id,
          tenantName: working?.tenant?.name,
          account: working?.account,
        })
      : notOnboardedOption;

  const accountDisplay =
    !isWorkingTenantOnboarded || !hasWorkingTenantId
      ? "Preview a Customer Account"
      : "SHI One Tenant";

  const tenantOptions = useMemo(() => {
    const onboarded = tenants
      .flatMap((tenant) =>
        tenant.accounts
          .filter((account) => account.isPrimary)
          .map((account) =>
            toTenantOption({
              tenantId: tenant.id,
              tenantName: tenant.name,
              account,
            }),
          ),
      )
      .sort((a, b) => {
        // Sort by name first, then by accountName
        const nameComparison = (a.name || "").localeCompare(b.name || "");
        if (nameComparison !== 0) return nameComparison;
        return (a.accountName || "").localeCompare(b.accountName || "");
      });

    const primaryAccount =
      pEffectiveTenant?.accounts.find((account) => account.isPrimary) ??
      pEffectiveTenant?.accounts[0] ??
      null;
    const effectiveOption: TenantOption | null = pEffectiveTenant
      ? toTenantOption({
          tenantId: pEffectiveTenant.id,
          tenantName: pEffectiveTenant.name,
          account: primaryAccount,
        })
      : null;

    const options = [...onboarded, notOnboardedOption];

    const canIncludeEffectiveOption =
      effectiveOption != null &&
      effectiveOption.id !== TENANT_ID_UNDEFINED &&
      isOnboardedTenant(pEffectiveTenant?.onBoardingStatus ?? null);

    if (canIncludeEffectiveOption) {
      prependOptionIfMissing(options, effectiveOption);
    }

    if (isWorkingTenantOnboarded) {
      prependOptionIfMissing(options, workingOption);
    }

    return options;
  }, [isWorkingTenantOnboarded, pEffectiveTenant, tenants, workingOption]);

  const ariaLabelSelection = [workingOption?.name, accountDisplay]
    .filter(Boolean)
    .join(" ");

  const handleSelectTenant = (selectedTenant: Tenant | null) => {
    if (!selectedTenant) return;

    const isOriginTenant = selectedTenant.id === origin?.tenant?.id;
    const isOriginPreviewTenant =
      selectedTenant.id === TENANT_ID_UNDEFINED &&
      !isOnboardedTenant(origin?.tenant?.onBoardingStatus ?? null);
    const isOriginAccountMatch = isOriginTenant || isOriginPreviewTenant;

    setImpersonationState((prev) => {
      const baseSelection = getWorkingSelection(prev);

      // Validate required context before proceeding
      if (!baseSelection) {
        return prev;
      }

      // Not Onboarded without an origin match recalls the last previewed
      // account (if any) instead of clearing the selection.
      const firstAccount = isOriginAccountMatch
        ? (origin?.account ?? null)
        : selectedTenant.id === TENANT_ID_UNDEFINED
          ? getPreviewAccount(prev)
          : (selectedTenant.accounts.find((account) => account.isPrimary) ??
            selectedTenant.accounts[0] ??
            null);

      const nextAccount: Account | null = firstAccount
        ? toAccount(firstAccount)
        : null;

      const cachedUser =
        nextAccount != null ? getPersonaForAccount(prev, nextAccount.id) : null;
      const originUser = isOriginAccountMatch ? (origin?.user ?? null) : null;

      // Restore a known persona (current, cached, or origin) before falling back;
      // only onboarded tenants default a fresh account to Virtual Admin.
      const nextUser =
        nextAccount == null
          ? null
          : nextAccount.id === baseSelection?.account?.id
            ? baseSelection.user
            : (cachedUser ??
              originUser ??
              (isOnboardedTenant(selectedTenant.onBoardingStatus)
                ? createVirtualAdminPersona(nextAccount.id)
                : null));

      const nextSelection: ImpersonationSelection = {
        ...baseSelection,
        tenant: {
          id: selectedTenant.id,
          name: selectedTenant.name,
          accounts: nextAccount ? [nextAccount] : [],
          onBoardingStatus: selectedTenant.onBoardingStatus,
        },
        account: nextAccount,
        user: nextUser,
      };

      return updateSelectionWithPersona(prev, nextSelection);
    });

    closeSelector();
  };

  return (
    <Box sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0 }}>
      <ClickableSection
        {...telemetryProps({
          name: "impersonation_tenant_selector",
          section: "impersonation_panel",
          label: "Tenant Selector",
        })}
        onClick={handleOpen}
        onKeyDown={handleSummaryKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-controls={LISTBOX_ID}
        aria-expanded={isOpen}
        aria-label={`Tenant selector. Selected: ${ariaLabelSelection}`}
        isActive={isOpen}
      >
        <TenantAvatar id={workingOption.id} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "fontWeightMedium", wordBreak: "break-word" }}
          >
            {workingOption.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {accountDisplay}
          </Typography>
        </Box>
      </ClickableSection>

      <SearchPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeSelector}
        onDebouncedSearchChange={setSearchString}
        onSearchKeyDown={handleSearchKeyDown}
        searchPlaceholder="Search tenants..."
        searchInputRef={searchInputRef}
        searchInputName="impersonation-tenant-search"
        searchInputAriaLabel="Search tenants"
        isSearching={isInitialLoading}
        searchTelemetryProps={telemetryProps({
          name: "search_impersonation_tenants",
          section: "impersonation_panel",
          label: "Search Tenants",
        })}
        listId={LISTBOX_ID}
        listAriaLabel="Tenant options"
        listRef={listRef}
        statusMessage={
          tenantOptions.length === 0
            ? "No tenants found"
            : `${tenantOptions.length} tenant(s) found`
        }
        isEmpty={tenantOptions.length === 0}
        emptyMessage="No tenants found"
        statusSx={srOnlySx}
      >
        {tenantOptions.map((option, index) => {
          const isSelected = option.id === workingOption.id;
          return (
            <ListItemButton
              key={
                option.rowKey ?? `${option.id}-${option.accountId ?? "account"}`
              }
              role="option"
              aria-selected={isSelected}
              selected={isSelected}
              onClick={() => {
                const selectedTenant =
                  option.id === pEffectiveTenant?.id
                    ? pEffectiveTenant
                    : (notOnboarded.find((t) => t.id === option.id) ??
                      tenants.find((t) => t.id === option.id) ??
                      (option.id === TENANT_ID_UNDEFINED
                        ? {
                            id: TENANT_ID_UNDEFINED,
                            name: "Not Onboarded",
                            accounts: [],
                            onBoardingStatus: null,
                          }
                        : null));
                handleSelectTenant(selectedTenant);
              }}
              onKeyDown={(e) =>
                handleOptionKeyDown(e, index, tenantOptions.length)
              }
              sx={{
                gap: 1.25,
                py: { xs: 1.25, sm: 1 },
                px: { xs: 1.5, sm: 2 },
              }}
            >
              <TenantAvatar id={option.id} size={3.5} />
              <ListItemText
                primary={option.name}
                secondary={option.accountName ?? undefined}
                slotProps={{
                  primary: { variant: "body2", fontWeight: "fontWeightMedium" },
                  secondary: { variant: "caption" },
                }}
              />
              {isSelected ? (
                <CheckIcon
                  fontSize="small"
                  sx={{ color: "primary.main", flexShrink: 0 }}
                />
              ) : null}
            </ListItemButton>
          );
        })}
      </SearchPopover>
    </Box>
  );
}
